<template>
	<BaseModal :title="t('title')" v-model="model">
		<template #leftAction>
			<div class="relative" v-hover="{ impact: 'soft' }" @click="openFairness">
				<ShieldIcon class="text-label-tertiary h-5 w-5" />
				<div class="absolute -inset-2" />
			</div>
		</template>
		<div
			class="w-full snap-x snap-mandatory scroll-pr-5 scroll-pl-5 overflow-x-scroll"
			@scroll="scroll"
		>
			<div class="flex w-fit gap-2 px-5">
				<ItemPull
					v-for="(item, index) in items"
					class="w-[calc(100vw-40px)] shrink-0 snap-center transition-opacity duration-300"
					:collection="item.collection"
					:name="item.name"
					:src="item.src"
					:price="item.price"
					:chance="item.chance"
					:color="getItemColor(item, casePrice)"
					:class="{ 'opacity-60': index !== active }"
					@sell="sell(item.uuid)"
					:sold="sold.includes(item.uuid)"
					:preloader="item.preloader"
					:cannotBeSold="cannotBeSold || (!item.claimable && items.length <= 1)"
					:cannotBeClaimed="!item.claimable"
					@share="share(item.uuid)"
					:sharing="sharing"
				/>
			</div>
		</div>
		<CarouselPills
			:active="active"
			:count="items.length"
			class="mt-4 w-full"
			v-if="items.length > 1"
		/>
		<div
			class="w-full px-4"
			:class="{ 'mt-4': items.length === 1, 'mt-6': items.length > 1 }"
			v-if="!cannotBeSold && !items.every((items) => !items.uuid)"
		>
			<BaseButton
				class="w-full"
				@click="sellAll"
				size="lg"
				color="primary"
				:loading="selling"
			>
				<div>{{ items.length === 1 ? t('sellOne') : t('sell') }}</div>
				<CoinPrice :price="value" />
			</BaseButton>
		</div>
		<div
			v-else
			class="text-label-tertiary flex items-center justify-center gap-1.5 py-4"
		>
			<ClaimIcon class="h-5 w-5" />
			<div class="text-sm font-semibold uppercase">
				{{ t('addedToBalance') }}
			</div>
		</div>
	</BaseModal>
</template>
<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import ItemPull from '../ItemPull.vue'
import BaseModal from './BaseModal.vue'
import CarouselPills from '../CarouselPills.vue'
import BaseButton from '../BaseButton.vue'
import CoinPrice from '../CoinPrice.vue'
import { useInventory } from '@/stores/inventory'
import { useI18n } from 'vue-i18n'
import ShieldIcon from '@/assets/icons/shield.svg'
import ClaimIcon from '@/assets/icons/claim.svg'
import { useToast } from '@/stores/toast'
import { getItemColor } from '@/utils/itemColor'

const { t } = useI18n({
	messages: {
		en: {
			title: 'your pulls',
			sell: 'sell all for',
			sellOne: 'sell for',
			addedToBalance: 'added to balance',
			failedToSell: 'Failed to sell selected gifts',
			failedToSellSome: 'Failed to sell {n} of the selected gifts'
		},
		ru: {
			title: 'твой дроп',
			sell: 'продать все за',
			sellOne: 'продать за',
			addedToBalance: 'зачислено на баланс',
			failedToSell: 'Не удалось продать выбранные гифты',
			failedToSellSome: 'Не удалось продать {n} из выбранных гифтов'
		}
	}
})

const props = defineProps<{
	items: {
		uuid: string
		src: string
		collection: string
		name: string
		price: number
		chance: number
		preloader: string | null
		claimable: boolean
	}[]
	cannotBeSold?: boolean
	sharing?: boolean
	casePrice: number
}>()

const emit = defineEmits<{
	(e: 'openFairness'): void
	(e: 'share', uuid: string): void
}>()

const model = defineModel<boolean>()

const active = ref(0)

watch(model, () => {
	if (!model.value) {
		return
	}

	active.value = 0
})

const scroll = (e: Event) => {
	const target = e.target as HTMLDivElement
	const scrollLeft = target.scrollLeft
	const scrollWidth = target.scrollWidth
	const clientWidth = target.clientWidth

	active.value = Math.round(
		(scrollLeft / (scrollWidth - clientWidth)) * (props.items.length - 1)
	)
}

const value = computed(() =>
	props.items
		.filter((item) => !sold.value.includes(item.uuid))
		.reduce((acc, item) => acc + item.price, 0)
)

const close = () => {
	model.value = false
}

const inventory = useInventory()

const selling = ref(false)
const sold = ref<string[]>([])
const toast = useToast()

const sell = async (uuid: string) => {
	// Remove the item from the list
	sold.value.push(uuid)

	if (sold.value.length === props.items.length) {
		close()
	}

	selling.value = true
	const soldItems = await inventory.sellItems([uuid])

	if (!soldItems.length) {
		toast.add({
			type: 'error',
			text: t('failedToSell')
		})
	}

	selling.value = false
}

const sellAll = async () => {
	close()

	selling.value = true

	const unsold = props.items
		.map((item) => item.uuid)
		.filter((uuid) => !sold.value.includes(uuid))

	const soldItems = await inventory.sellItems(unsold)

	if (!soldItems.length) {
		toast.add({
			type: 'error',
			text: t('failedToSell')
		})
	}
	if (soldItems.length !== unsold.length) {
		toast.add({
			type: 'error',
			text: t('failedToSellSome', unsold.length - soldItems.length)
		})
	}

	selling.value = false

	sold.value = props.items.map((item) => item.uuid)
}

const openFairness = () => {
	emit('openFairness')
}

const share = async (uuid: string) => {
	emit('share', uuid)
}
</script>
